interface CommonSchemaBuildingProps {
  pageType: string;
  pid: string;
}

interface DetailSchemaBuildingProps extends CommonSchemaBuildingProps {
  groupId: string;
  authorId?: string;
}

interface ArticleSchemaBuildingProps extends DetailSchemaBuildingProps {
  pageType: 'article';
}

const isArticleSchemaBuildingProps = (input: CommonSchemaBuildingProps): input is ArticleSchemaBuildingProps =>
  input.pageType === 'article';

interface VideoSchemaBuildingProps extends DetailSchemaBuildingProps {
  pageType: 'video';
}

const isVideoSchemaBuildingProps = (input: CommonSchemaBuildingProps): input is VideoSchemaBuildingProps =>
  input.pageType === 'video';

export interface UserSchemaBuildingProps extends CommonSchemaBuildingProps {
  pageType: 'user';
  userId: string;
}

const isUserSchemaBuildingProps = (input: CommonSchemaBuildingProps): input is UserSchemaBuildingProps =>
  input.pageType === 'user';

interface CategorySchemaBuildingProps extends CommonSchemaBuildingProps {
  pageType: 'feed';
  categoryId: number;
}

const isCategorySchemaBuildingProps = (input: CommonSchemaBuildingProps): input is CategorySchemaBuildingProps =>
  input.pageType === 'feed';

export interface HashtagSchemaBuildingProps extends CommonSchemaBuildingProps {
  pageType: 'hashtag';
  hashtagId: string;
}

const isHashtagSchemaBuildingProps = (input: CommonSchemaBuildingProps): input is HashtagSchemaBuildingProps =>
  input.pageType === 'hashtag';

export interface POISchemaBuildingProps extends CommonSchemaBuildingProps {
  pageType: 'poi';
  poiId: string;
}

const isPOISchemaBuildingProps = (input: CommonSchemaBuildingProps): input is POISchemaBuildingProps =>
  input.pageType === 'poi';

export interface SearchSchemaBuildingProps extends CommonSchemaBuildingProps {
  pageType: 'search';
  query: string;
}

const isSearchSchemaBuildingProps = (input: CommonSchemaBuildingProps): input is SearchSchemaBuildingProps =>
  input.pageType === 'search';

const buildSearchSchema = (props: SearchSchemaBuildingProps): string => {
  const { query } = props;

  const baseUrl =
    'sslocal://lynxview?channel=search_v2&trans_status_bar=1&enter_anim=search_alpha&exit_anim=search_alpha&business_type=search&bundle=result_page%2Ftemplate.js';

  const data = {
    query,
    tab: 0,
    inputClickType: 'open',
    logExtra: {
      query,
      search_page_from: 'search_page_general',
      search_from: 'seo_click',
      previous_page_name: 'seo',
    },
  };

  return `${baseUrl}&business_data=${encodeURIComponent(JSON.stringify(data))}`;
};

export type DeepLinkBuildingProps =
  | ArticleSchemaBuildingProps
  | VideoSchemaBuildingProps
  | UserSchemaBuildingProps
  | CategorySchemaBuildingProps
  | HashtagSchemaBuildingProps
  | POISchemaBuildingProps
  | SearchSchemaBuildingProps;

export const buildSchema = (props: DeepLinkBuildingProps): string => {
  // 唤活到搜索
  if (isSearchSchemaBuildingProps(props)) {
    return buildSearchSchema(props);
  }

  let protocol = 'snssdk2657:';
  let host = 'unknown';
  let params: URLSearchParams;

  // if (__BUILD_TYPE__ !== 'online') {
  //   protocol = 'snssdk2657test:';
  // }

  if (isArticleSchemaBuildingProps(props)) {
    host = 'article_detail_page';
    params = new URLSearchParams({
      group_id: props.groupId,
    });
    if (props.authorId) {
      params.set('media_id', props.authorId);
    }
  } else if (isVideoSchemaBuildingProps(props)) {
    host = 'video_detail';
    params = new URLSearchParams({
      group_id: props.groupId,
    });
    if (props.authorId) {
      params.set('media_id', props.authorId);
    }
  } else if (isUserSchemaBuildingProps(props)) {
    host = 'user_profile';
    params = new URLSearchParams({
      user_id: props.userId,
    });
  } else if (isCategorySchemaBuildingProps(props)) {
    host = 'main';
    params = new URLSearchParams({
      tab: 'home',
      channel_id: String(props.categoryId),
    });
  } else if (isHashtagSchemaBuildingProps(props)) {
    host = 'hashtag';
    params = new URLSearchParams({
      hashtag_id: props.hashtagId,
    });
  } else if (isPOISchemaBuildingProps(props)) {
    host = 'poi_landing_page';
    params = new URLSearchParams({
      poi_id: props.poiId,
    });
  } else {
    throw new Error('unexcepted schema target');
  }

  // append common params
  params.append('pid', props.pid);

  return `${protocol}//${host}?${params.toString()}`;
};
